/**
 * Sticky site header with a keyboard-accessible mega menu and a mobile sheet.
 *
 * Navigation is entirely CMS-driven (`Menu` / `MenuItem` records), so an administrator can add a
 * solution or reorder a panel without a deployment. Items with children open a panel; items
 * without are plain links.
 *
 * Accessibility: each trigger is a real `<button>` with `aria-expanded` and `aria-controls`,
 * hovering opens the panel only for pointer users, and `Escape` closes it and returns focus.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Button, ButtonLink } from '@/components/ui';
import { Logo } from '@/components/site/Logo';
import { SearchDialog } from '@/components/site/SearchDialog';
import { useMenu, useSiteSettings } from '@/context/SiteContext';
import { useClickOutside, useEscapeKey, useScrollLock, useScrolled } from '@/hooks';
import { cn } from '@/lib/cn';
import type { MenuItem } from '@/services/types';

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 12 12"
      className={cn('h-3 w-3 transition-transform duration-micro', open && 'rotate-180')}
    >
      <path d="M2 4.5 6 8.5l4-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MegaPanel({ item, id, onNavigate }: { item: MenuItem; id: string; onNavigate: () => void }) {
  const columns = item.children.length > 6 ? 3 : 2;
  return (
    <div
      id={id}
      className="absolute left-0 right-0 top-full z-[60] animate-fade-in border-b border-line bg-surface shadow-float"
    >
      <div className="shell grid gap-8 py-8 lg:grid-cols-[2fr_1fr]">
        <ul className={cn('grid gap-1', columns === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2')}>
          {item.children.map((child) => (
            <li key={child.id}>
              <Link
                to={child.href || child.url}
                onClick={onNavigate}
                className="group flex flex-col gap-1 rounded-md p-3 no-underline transition-colors duration-micro hover:bg-raised hover:no-underline"
              >
                <span className="flex items-center gap-2 text-[0.9375rem] font-semibold text-ink group-hover:text-primary">
                  {child.label}
                  {child.badge && (
                    <span className="rounded-full bg-accent-soft px-2 py-0.5 text-[0.625rem] font-bold uppercase text-accent">
                      {child.badge}
                    </span>
                  )}
                </span>
                {child.description && <span className="text-sm leading-snug text-muted">{child.description}</span>}
              </Link>
            </li>
          ))}
        </ul>
        <div className="rounded-lg border border-line bg-raised p-6">
          <p className="text-eyebrow uppercase text-primary">Not sure where to start?</p>
          <p className="mt-3 font-display text-lg font-semibold text-ink">
            Tell us the problem, not the product category.
          </p>
          <p className="mt-2 text-sm text-muted">
            A short conversation with our engineers is usually faster than reading six pages.
          </p>
          <ButtonLink to="/contact" size="sm" className="mt-4" onClick={onNavigate}>
            Talk to our experts
          </ButtonLink>
        </div>
      </div>
    </div>
  );
}

function MobileSheet({ items, open, onClose }: { items: MenuItem[]; open: boolean; onClose: () => void }) {
  const settings = useSiteSettings();
  const panelRef = useRef<HTMLDivElement>(null);

  useScrollLock(open);
  useEscapeKey(onClose, open);

  useEffect(() => {
    if (open) panelRef.current?.querySelector<HTMLElement>('a, button')?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] lg:hidden">
      <button type="button" aria-label="Close menu" onClick={onClose} className="absolute inset-0 bg-ink/50" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Site navigation"
        className="absolute inset-y-0 right-0 flex w-full max-w-sm animate-slide-in-right flex-col bg-surface shadow-float"
      >
        <div className="flex items-center justify-between border-b border-line px-5 py-4">
          <Logo />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="rounded p-2 text-muted hover:bg-raised hover:text-ink focus-visible:ring-2 focus-visible:ring-primary"
          >
            <span aria-hidden="true">✕</span>
          </button>
        </div>

        <nav aria-label="Mobile navigation" className="flex-1 overflow-y-auto px-3 py-4">
          <ul className="space-y-1">
            {items.map((item) =>
              item.children.length > 0 ? (
                <li key={item.id}>
                  <details className="group rounded-md [&_summary::-webkit-details-marker]:hidden">
                    <summary className="flex cursor-pointer items-center justify-between rounded-md px-3 py-3 font-semibold text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                      {item.label}
                      <span aria-hidden="true" className="text-primary transition-transform group-open:rotate-45">
                        +
                      </span>
                    </summary>
                    <ul className="ml-3 border-l border-line pl-3">
                      {item.children.map((child) => (
                        <li key={child.id}>
                          <Link
                            to={child.href || child.url}
                            onClick={onClose}
                            className="block rounded px-3 py-2.5 text-[0.9375rem] text-ink-soft no-underline hover:bg-raised hover:no-underline"
                          >
                            {child.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </details>
                </li>
              ) : (
                <li key={item.id}>
                  <Link
                    to={item.href || item.url}
                    onClick={onClose}
                    className="block rounded-md px-3 py-3 font-semibold text-ink no-underline hover:bg-raised hover:no-underline"
                  >
                    {item.label}
                  </Link>
                </li>
              ),
            )}
          </ul>
        </nav>

        <div className="space-y-3 border-t border-line px-5 py-4">
          <ButtonLink to="/contact" fullWidth onClick={onClose}>
            {settings.secondary_cta_label || 'Talk to Our Experts'}
          </ButtonLink>
          {settings.primary_phone && (
            <a href={`tel:${settings.primary_phone.replace(/\s/g, '')}`} className="block text-center text-sm text-muted">
              {settings.primary_phone}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export function Header() {
  const items = useMenu('primary');
  const settings = useSiteSettings();
  const scrolled = useScrolled();
  const location = useLocation();

  const [openPanel, setOpenPanel] = useState<number | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const closePanel = useCallback(() => setOpenPanel(null), []);
  const navRef = useClickOutside<HTMLDivElement>(closePanel, openPanel !== null);
  useEscapeKey(closePanel, openPanel !== null);

  // Any navigation closes every overlay — otherwise a panel stays open over the new page.
  useEffect(() => {
    setOpenPanel(null);
    setMobileOpen(false);
  }, [location.pathname]);

  // Cmd/Ctrl-K opens search, the shortcut people expect.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <>
      <a href="#main" className="skip-link">
        Skip to main content
      </a>

      <header
        className={cn(
          'sticky top-0 z-[50] border-b transition-[background-color,border-color,box-shadow] duration-standard',
          scrolled ? 'border-line bg-surface/90 shadow-subtle backdrop-blur-md' : 'border-transparent bg-surface',
        )}
      >
        <div ref={navRef} className="relative">
          <div className="shell flex h-[var(--header-height)] items-center justify-between gap-4">
            <Logo />

            <nav aria-label="Primary" className="hidden lg:block">
              <ul className="flex items-center gap-0.5">
                {items.map((item) => {
                  const hasChildren = item.children.length > 0;
                  const panelId = `mega-${item.id}`;
                  const isOpen = openPanel === item.id;

                  if (!hasChildren) {
                    return (
                      <li key={item.id}>
                        <NavLink
                          to={item.href || item.url}
                          className={({ isActive }) =>
                            cn(
                              'rounded-md px-3.5 py-2 text-[0.9375rem] font-medium no-underline transition-colors duration-micro hover:no-underline',
                              isActive ? 'text-primary' : 'text-ink hover:text-primary',
                            )
                          }
                        >
                          {item.label}
                        </NavLink>
                      </li>
                    );
                  }

                  return (
                    <li key={item.id} onMouseEnter={() => setOpenPanel(item.id)} onMouseLeave={closePanel}>
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        aria-controls={panelId}
                        onClick={() => setOpenPanel(isOpen ? null : item.id)}
                        className={cn(
                          'flex items-center gap-1.5 rounded-md px-3.5 py-2 text-[0.9375rem] font-medium transition-colors duration-micro',
                          isOpen ? 'text-primary' : 'text-ink hover:text-primary',
                        )}
                      >
                        {item.label}
                        <ChevronIcon open={isOpen} />
                      </button>
                      {isOpen && <MegaPanel item={item} id={panelId} onNavigate={closePanel} />}
                    </li>
                  );
                })}
              </ul>
            </nav>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                aria-label="Search the site"
                className="rounded-md p-2.5 text-muted transition-colors hover:bg-raised hover:text-ink focus-visible:ring-2 focus-visible:ring-primary"
              >
                <svg aria-hidden="true" viewBox="0 0 20 20" className="h-5 w-5">
                  <path
                    d="M13 13l4 4M15 8.5a6.5 6.5 0 1 1-13 0 6.5 6.5 0 0 1 13 0Z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  />
                </svg>
              </button>

              <ButtonLink to={settings.secondary_cta_url || '/contact'} size="sm" className="hidden sm:inline-flex">
                {settings.secondary_cta_label || 'Talk to Our Experts'}
              </ButtonLink>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileOpen(true)}
                aria-label="Open menu"
                aria-expanded={mobileOpen}
                className="lg:hidden"
              >
                <svg aria-hidden="true" viewBox="0 0 20 20" className="h-5 w-5">
                  <path d="M3 6h14M3 10h14M3 14h14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                </svg>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <MobileSheet items={items} open={mobileOpen} onClose={() => setMobileOpen(false)} />
      <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
